import { useCallback, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import { Button, CircularProgress, Stack, useTheme } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { PropertySearchesList } from '@modules/_shared/components/property-search/PropertySearchesList';
import { usePropertySearchQuickFilters } from '@modules/_shared/components/property-search/usePropertySearchQuickFilters';
import { isDeepEqual } from '@modules/_shared/utils/objects';
import { UPDATE_AUTO_MATCHING_FILTERS } from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';

type AutoMatchingCriteriaDrawerProps = {
  listingId: string;
  onClose: () => void;
  initialCriteria: any | null | undefined;
  offerType: string | null | undefined;
};

export const AutoMatchingCriteriaDrawer: React.FC<
  AutoMatchingCriteriaDrawerProps
> = ({ listingId, onClose, initialCriteria, offerType }) => {
  const { t } = useLocale();
  const { breakpoints } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [updateAutoMatchingFilters, { loading: updateLoading }] = useMutation(
    UPDATE_AUTO_MATCHING_FILTERS,
  );

  const quickFilters = usePropertySearchQuickFilters({
    disableOfferType: true,
  });

  const handleCloseDrawer = useCallback(() => {
    searchParams.delete('auto-matching_where');
    setSearchParams(searchParams);
    onClose();
  }, [searchParams, setSearchParams, onClose]);

  const criteriasHaveChanged = useMemo(() => {
    return !isDeepEqual(
      initialCriteria ?? {},
      JSON.parse(searchParams.get('auto-matching_where') ?? '{}'),
    );
  }, [initialCriteria, searchParams]);

  return (
    <Drawer
      onClose={handleCloseDrawer}
      title={t('Edit auto matching criteria')}
      maxWidth={breakpoints.values.lg}
    >
      <Stack sx={{ height: '100%' }}>
        <PropertySearchesList
          hideTitle
          queryParamsScope="auto-matching"
          quickFilters={quickFilters}
          defaultFilters={{
            offer_type:
              offerType != null
                ? {
                    _in: [offerType === 'sell' ? 'buy' : 'rent'],
                  }
                : undefined,
          }}
          toolbarActions={
            <Stack
              spacing={1}
              direction="row"
              sx={{
                p: 1,
              }}
            >
              <LoadingButton
                variant="contained"
                color="primary"
                loading={updateLoading}
                disabled={!criteriasHaveChanged}
                loadingIndicator={
                  <CircularProgress disableShrink color="inherit" size={16} />
                }
                onClick={() => {
                  updateAutoMatchingFilters({
                    variables: {
                      id: listingId,
                      filters: JSON.parse(
                        searchParams.get('auto-matching_where') ?? '{}',
                      ),
                    },
                    onError: setErrorLike,
                    onCompleted: ({ update_lots_by_pk }) => {
                      if (update_lots_by_pk) {
                        handleCloseDrawer();
                      }
                    },
                  });
                }}
              >
                {t('Save criteria')}
              </LoadingButton>
              <Button variant="outlined" onClick={handleCloseDrawer}>
                {t('Cancel')}
              </Button>
            </Stack>
          }
        />
      </Stack>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </Drawer>
  );
};
